import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { BigQueryAuthService } from './big-query-auth.service';

export const EXTERNAL_LOGGER_SERVICE_URL = new InjectionToken<string>(
  'EXTERNAL_LOGGER_SERVICE_URL',
  {
    providedIn: 'root',
    factory: () =>
      'https://www.googleapis.com/bigquery/v2/projects/client-side-events/datasets/Apps_Events/tables/TABLE_ID/insertAll',
  },
);

export const ENABLE_EXTERNAL_LOGGING = new InjectionToken<boolean>(
  'ENABLE_EXTERNAL_LOGGING',
  {
    providedIn: 'root',
    factory: () => true,
  },
);

interface ExternalLoggerInsertRequest {
  kind: 'bigquery#tableDataInsertAllRequest';
  skipInvalidRows: false;
  ignoreUnknownValues: false;
  templateSuffix: string;
  rows: [
    {
      insertId: string;
      json: {
        event: string;
        event_details: unknown;
        event_value: unknown;
        host: string;
        ts: string | number;
        user_id: string;
        company_id: string;
      };
    },
  ];
}

@Injectable({
  providedIn: 'root',
})
export class ExternalLoggingService {
  private readonly http = inject(HttpClient);
  private readonly bigQueryAuth = inject(BigQueryAuthService);
  private readonly serviceUrl = inject(EXTERNAL_LOGGER_SERVICE_URL);
  private readonly enabled = inject(ENABLE_EXTERNAL_LOGGING);
  private readonly insertSchema: ExternalLoggerInsertRequest = {
    kind: 'bigquery#tableDataInsertAllRequest',
    skipInvalidRows: false,
    ignoreUnknownValues: false,
    templateSuffix: this.getSuffix(),
    rows: [
      {
        insertId: '',
        json: {
          event: '',
          event_details: '',
          event_value: 0,
          host: '',
          ts: 0,
          user_id: '',
          company_id: '',
        },
      },
    ],
  };

  logEvent(
    eventName: string,
    eventDetails?: unknown,
    eventValue?: unknown,
    userId?: string | null,
    companyId?: string | null,
  ): Promise<HttpResponse<unknown>> | undefined {
    const endpointId = companyId?.trim();
    if (!this.enabled || !endpointId) {
      console.debug('External Logging DISABLED');
      return;
    }

    return this.bigQueryAuth.getToken().then(
      (token) => {
        const insertData: ExternalLoggerInsertRequest = JSON.parse(
          JSON.stringify(this.insertSchema),
        );
        const url = this.serviceUrl.replace('TABLE_ID', 'apps_events');

        insertData.rows[0].insertId = Math.random()
          .toString(36)
          .substring(2)
          .toUpperCase();
        insertData.rows[0].json.event = eventName;
        if (eventDetails) {
          insertData.rows[0].json.event_details = eventDetails;
        }
        if (eventValue) {
          insertData.rows[0].json.event_value = eventValue;
        }
        insertData.rows[0].json.user_id = userId || '';
        insertData.rows[0].json.company_id = companyId || '';
        insertData.rows[0].json.host = window.location.hostname;
        insertData.rows[0].json.ts = new Date().toISOString();

        return firstValueFrom(
          this.http.post(url, insertData, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            observe: 'response',
          }),
        ).catch((error) => {
          return Promise.reject(error);
        });
      },
      (error) => {
        return Promise.reject(error);
      },
    );
  }

  private getSuffix(): string {
    const date = new Date();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');

    return `${date.getUTCFullYear()}${month}${day}`;
  }
}
